import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { combineStrings } from 'assets/utils/data/String';
import { ContractDraft, SalesNewContractAction } from '../../newContract/Sales.NewContract.Hooks';
import { salesNewContractIndexRoute } from '../../newContract/Sales.NewContract.Index';
import { SalesDraftAction, useSalesDraftProvider } from '../Sales.Draft.Hooks';
import Box from 'assets/components/box/Box';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export default function DraftList() {
  const { lang } = getLocales();
  const { listApi, pageRouter, permissions } = useSalesDraftProvider();
  const { redirect: redirectToNewContract } = usePageRouter<
    Module.Sales.NewContract.Params,
    Module.Sales.NewContract.Query
  >({ route: salesNewContractIndexRoute });

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.drafts}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      isLoading={listApi.isExecuting}
      header={<h2 style={{ marginRight: 'auto' }} children={lang.drafts} />}
      render={
        <table className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>{lang.appNumber}</th>
              <th>{lang.vehicle}</th>
              <th>{lang.vin}</th>
              <th>{lang.client}</th>
              <th>{lang.dealer}</th>
              <th>{lang.createdAt}</th>
              <th data-actions>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: draft }) => {
                const contractDraft = draft.data as ContractDraft;
                const selectedProducts = contractDraft?.products?.map((it) => it.code);
                return (
                  contractDraft && (
                    <tr key={draft.id}>
                      <td>
                        <b>{selectedProducts?.join(', ') || lang.unknownProduct}</b>
                      </td>
                      <td>
                        {combineStrings(
                          '/',
                          contractDraft.vehicle?.year?.toString(),
                          contractDraft.vehicle?.makeCode,
                          contractDraft.vehicle?.model
                        )}
                      </td>
                      <td>{contractDraft.vehicle?.vin}</td>
                      <td>{combineStrings(' ', contractDraft.client?.firstName, contractDraft.client?.lastName)}</td>
                      <td>{contractDraft.dealer?.name}</td>
                      <td>
                        <span>{localizeDate(contractDraft?.dateUpdated, defaultDateTimeFormat)}</span>
                      </td>
                      <td>
                        <Box class={ThemeWrapperStack}>
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-edit" />}
                            onClick={() =>
                              redirectToNewContract({ action: SalesNewContractAction.VEHICLE }, { draftId: draft.id })
                            }
                            disabled={!permissions.CREATE_CONTRACT}
                            active
                            htmlElementProps={{ title: lang.edit }}
                          />
                          <Button
                            onClick={pageRouter.updateParams}
                            data={{
                              id: draft.id.toString(),
                              action: SalesDraftAction.DELETE,
                            }}
                            disabled={!permissions.CREATE_CONTRACT}
                            active
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-trash" />}
                            htmlElementProps={{ title: lang.delete }}
                          />
                        </Box>
                      </td>
                    </tr>
                  )
                );
              }}
            />
          </tbody>
        </table>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) => listApi.execute((body) => ({ ...body, limit, offset: 0 }))}
          onOffsetChange={(offset) => listApi.execute((body) => ({ ...body, offset }))}
        />
      }
    />
  );
}
